import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AppWindow, Plus, Pencil, Trash2, Loader2, ExternalLink, MonitorSmartphone,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const empty = { name: "", website: "", logo_url: "", platforms: "", notes: "" };

export default function Applications() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [apps, setApps] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    try { const { data } = await api.get("/applications"); setApps(data); }
    catch { toast.error("Failed to load applications"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name || "", website: a.website || "", logo_url: a.logo_url || "", platforms: a.platforms || "", notes: a.notes || "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) { await api.put(`/applications/${editing.id}`, form); toast.success("Application updated"); }
      else { await api.post("/applications", form); toast.success("Application added"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    try { await api.delete(`/applications/${a.id}`); toast.success("Application deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openSite = (url) => {
    if (!url) return toast.error("No website set for this application");
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="applications-page">
      <PageHeader overline="Player Apps" title="Applications" description="Manage IPTV player apps and open their download pages in one click.">
        {isAdmin && <Button onClick={openCreate} data-testid="add-application-button"><Plus className="h-4 w-4 mr-2" /> New Application</Button>}
      </PageHeader>

      {!apps ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-lg" />)}
        </div>
      ) : apps.length === 0 ? (
        <Card className="p-12 text-center border-border">
          <AppWindow className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No applications yet.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {apps.map((a) => (
            <Card key={a.id} className="p-5 border-border bg-card flex flex-col" data-testid={`app-card-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className="grid place-items-center h-12 w-12 rounded-md bg-primary/10 text-primary overflow-hidden shrink-0">
                  {a.logo_url ? <img src={a.logo_url} alt={a.name} className="h-full w-full object-cover" /> : <AppWindow className="h-6 w-6" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-semibold text-lg truncate">{a.name}</h3>
                  {a.platforms && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MonitorSmartphone className="h-3 w-3" /> {a.platforms}
                    </p>
                  )}
                </div>
              </div>
              {a.notes && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{a.notes}</p>}
              <div className="mt-auto pt-4 flex items-center gap-2">
                <Button variant="secondary" size="sm" className="flex-1" onClick={() => openSite(a.website)} data-testid={`app-open-${a.id}`}>
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Open Website
                </Button>
                {isAdmin && (
                  <>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`app-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`app-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-popover">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {a.name}?</AlertDialogTitle>
                          <AlertDialogDescription>Customers assigned to this app will be unassigned. This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(a)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Application" : "New Application"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} placeholder="IBO Player" data-testid="app-name" /></div>
            <div className="space-y-2"><Label>Website URL</Label><Input value={form.website} onChange={set("website")} placeholder="https://iboplayer.com" data-testid="app-website" /></div>
            <div className="space-y-2"><Label>Logo URL <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={form.logo_url} onChange={set("logo_url")} placeholder="https://…/logo.png" data-testid="app-logo" /></div>
            <div className="space-y-2"><Label>Supported platforms <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={form.platforms} onChange={set("platforms")} placeholder="Smart TV, Android, iOS" data-testid="app-platforms" /></div>
            <div className="space-y-2"><Label>Notes <span className="text-muted-foreground font-normal">(optional)</span></Label><Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="app-notes" /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving || !form.name} data-testid="app-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
